// Pure helpers for Qualify AURA Step A (credit re-issue), aligned with the Add New
// Loan wizard (components/loan-form/credit-reissue-application): one credit
// credentials form per borrower pair, and the same Encompass field writes after a
// successful re-issue. Kept free of React/RTK so they can be unit-tested.
import { CREDIT_REISSUE_CONSTANTS, DEFAULT_CREDIT_CREDENTIALS_FORM } from "./credit-reissue-constants";
import { PROPERTY_FIELD_IDS } from "./current-loan-encompass-field-ids";

// Encompass loans on this portal carry at most three borrower pairs.
export const MAX_CREDIT_REISSUE_PAIRS = 3;

export const REISSUE_STATUS = {
	PENDING: "pending",
	SUCCESS: "success",
	FAILED: "failed",
};

// Never persisted to Redux/localStorage: the password, the saved-vendor encrypted
// password and the saved-vendor subscriber id.
const SECRET_CREDENTIAL_KEYS = ["password", "encryptedPassword", "prepopulatedSubscriberId"];

// Same two fields the wizard writes after each pair's re-issue succeeds
// (credit-reissue-application checkReissueStatus): credit report identifier and vendor.
// Plus "How will credit be provided?" = Reissue, which the Dashboard's Step 2 reads
// (and locks) — Qualify replaces the wizard step that would otherwise set it.
export const creditProvidedReissueField = () => ({
	id: PROPERTY_FIELD_IDS.creditReissueMethod,
	value: CREDIT_REISSUE_CONSTANTS.REISSUE,
});

export const buildCreditReissueFieldValues = (pairIndex, credentials) => [
	{ id: `300#${pairIndex + 1}`, value: credentials?.creditReportIdentifier || "" },
	{ id: `CX.LC.CreditVendor.Pair${pairIndex + 1}`, value: credentials?.creditVendor || "" },
	creditProvidedReissueField(),
];

// Credit was re-issued (this session or earlier) but "How will credit be provided?" is
// still blank — fill it with Reissue. Never overwrites a value the broker already chose.
export const needsCreditProvidedReissue = (currentValue, hasReissuedPair) =>
	Boolean(hasReissuedPair) && !String(currentValue ?? "").trim();

export const toPersistableCredentials = (credentialsList) =>
	(credentialsList || []).map((credentials) => {
		const safe = { ...(credentials || {}) };
		SECRET_CREDENTIAL_KEYS.forEach((key) => delete safe[key]);
		// A masked saved-vendor subscriber id ("***") is not a real value.
		if (safe.subscriberId === "***") safe.subscriberId = "";
		return safe;
	});

// Initial per-pair forms: the saved array when present, otherwise pair 1 seeded
// from the legacy single-pair qualifyInputs fields.
export const seedCreditCredentials = (savedInputs = {}) => {
	if (Array.isArray(savedInputs.creditCredentials) && savedInputs.creditCredentials.length) {
		return savedInputs.creditCredentials.map((credentials) => ({
			...DEFAULT_CREDIT_CREDENTIALS_FORM,
			...credentials,
			password: "",
		}));
	}
	return [
		{
			...DEFAULT_CREDIT_CREDENTIALS_FORM,
			creditVendor: savedInputs.creditVendor || "",
			creditReportIdentifier: savedInputs.reportRefId || "",
			reportOn: savedInputs.reportOn || "",
			clientID: savedInputs.clientId || "",
			subscriberId: savedInputs.subscriberId || "",
			userName: savedInputs.loginId || "",
			productCode: savedInputs.productCode || "",
			providerId: savedInputs.providerId || "",
		},
	];
};

// Grows/shrinks the per-pair forms to pairCount, keeping what was already entered.
export const resizeCreditCredentials = (credentialsList, pairCount) => {
	const count = Math.max(1, Math.min(MAX_CREDIT_REISSUE_PAIRS, pairCount || 1));
	return Array.from({ length: count }, (_, index) =>
		credentialsList?.[index] || { ...DEFAULT_CREDIT_CREDENTIALS_FORM }
	);
};

// Flat [borrower, coBorrower, ...] list in the shape CreditCredentialsInformation
// reads (loanApplicationData[pair * 2 + 1].firstName decides the Joint default).
export const toLoanApplicationPeople = (pairs) =>
	(pairs || []).slice(0, MAX_CREDIT_REISSUE_PAIRS).flatMap((pair) => [
		pair?.borrower || {},
		pair?.coborrower || pair?.coBorrower || {},
	]);

export const allPairsReissued = (statuses, pairCount) =>
	pairCount > 0 &&
	Array.from({ length: pairCount }, (_, index) => statuses?.[index]).every(
		(status) => status === REISSUE_STATUS.SUCCESS
	);

// Next pair still needing a successful re-issue after pairIndex (wraps), or -1.
export const nextPairToReissue = (statuses, pairCount, pairIndex) => {
	for (let step = 1; step <= pairCount; step += 1) {
		const candidate = (pairIndex + step) % pairCount;
		if (statuses?.[candidate] !== REISSUE_STATUS.SUCCESS) return candidate;
	}
	return -1;
};
